import { useState, useEffect } from 'react';
import { Song, Artist, User } from '../types';
import { mockSongs, mockArtists, mockUser } from '../data/mockData';

// Hook for persisted songs data
export const usePersistedSongs = () => {
  const [songs, setSongs] = useState<Song[]>(() => {
    const saved = localStorage.getItem('naad-songs');
    return saved ? JSON.parse(saved) : mockSongs;
  });

  useEffect(() => {
    localStorage.setItem('naad-songs', JSON.stringify(songs));
  }, [songs]);

  const addSong = (song: Song) => {
    setSongs(prev => [...prev, song]);
  };

  const updateSong = (id: string, updates: Partial<Song>) => {
    setSongs(prev => prev.map(song => 
      song.id === id ? { ...song, ...updates } : song
    ));
  };

  const deleteSong = (id: string) => {
    setSongs(prev => prev.filter(song => song.id !== id));
  };

  return { songs, setSongs, addSong, updateSong, deleteSong };
};

// Hook for persisted artists data
export const usePersistedArtists = () => {
  const [artists, setArtists] = useState<Artist[]>(() => {
    const saved = localStorage.getItem('naad-artists');
    return saved ? JSON.parse(saved) : mockArtists;
  });

  useEffect(() => {
    localStorage.setItem('naad-artists', JSON.stringify(artists));
  }, [artists]);

  const addArtist = (artist: Artist) => {
    setArtists(prev => [...prev, artist]);
  };

  const updateArtist = (id: string, updates: Partial<Artist>) => {
    setArtists(prev => prev.map(artist => 
      artist.id === id ? { ...artist, ...updates } : artist
    ));
  };

  const deleteArtist = (id: string) => {
    setArtists(prev => prev.filter(artist => artist.id !== id));
  };

  return { artists, setArtists, addArtist, updateArtist, deleteArtist };
};

// Hook for persisted user data
export const usePersistedUser = () => {
  const [user, setUser] = useState<User>(() => {
    const saved = localStorage.getItem('naad-user');
    return saved ? JSON.parse(saved) : mockUser;
  });

  useEffect(() => {
    localStorage.setItem('naad-user', JSON.stringify(user));
  }, [user]);

  const updateUser = (updates: Partial<User>) => {
    setUser(prev => ({ ...prev, ...updates }));
  };

  return { user, setUser, updateUser };
};

// Global data context
export interface AppData {
  songs: Song[];
  artists: Artist[];
  user: User;
  addSong: (song: Song) => void;
  updateSong: (id: string, updates: Partial<Song>) => void;
  deleteSong: (id: string) => void;
  addArtist: (artist: Artist) => void;
  updateArtist: (id: string, updates: Partial<Artist>) => void;
  deleteArtist: (id: string) => void;
  updateUser: (updates: Partial<User>) => void;
}

// Reset all data to mock defaults
export const resetAllData = () => {
  localStorage.removeItem('naad-songs');
  localStorage.removeItem('naad-artists');
  localStorage.removeItem('naad-user');
  window.location.reload();
};